import { useState } from "react";
import { useLastschrift, useQontoMandatAnlegen } from "../hooks/useLastschrift";
import { fehlertext } from "../lib/fehler";
import "../styles/lastschrift.css";

const tag = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");
const euro = (s: string | null) => (s ? `${s.replace(".", ",")} €` : "—");

/**
 * Woran hängt die Lastschrift dieses Vertrages?
 *
 * Seit 22.09.2026 entsteht das Mandat bei Qonto: der Kunde bestätigt es nach
 * der Zustimmung auf Qontos Seite. Bis dahin wird nichts eingezogen — die
 * Karte zeigt, ob das schon geschehen ist, und gibt den Link zum Weitergeben
 * (falls der Kunde die Mail nicht findet).
 */
export default function LastschriftKarte({ vertragId, status }: { vertragId: string; status: string }) {
  const relevant = ["zugestimmt", "aktiv", "gekuendigt"].includes(status);
  const { data } = useLastschrift(vertragId, relevant);
  const anlegen = useQontoMandatAnlegen(vertragId);
  const [kopiert, setKopiert] = useState(false);

  if (!relevant || !data || data.zahlungsart !== "lastschrift") return null;
  const m = data.mandat;

  let zustand: { klasse: string; text: string };
  if (!m) zustand = { klasse: "ls-offen", text: "Noch kein Mandat" };
  else if (m.weg === "eigen") zustand = { klasse: "ls-gut", text: `Eigenes Mandat · ${m.iban || "ohne IBAN"}` };
  else if (m.qonto_status === "approved") zustand = { klasse: "ls-gut", text: `Bei Qonto bestätigt · ${m.iban || "IBAN folgt"}` };
  else if (m.qonto_status === "pending_signature") zustand = { klasse: "ls-offen", text: "Wartet auf die Unterschrift des Kunden bei Qonto" };
  else if (m.qonto_status === "canceled") zustand = { klasse: "ls-rot", text: "Bei Qonto storniert" };
  else if (data.einzugsweg !== "qonto") zustand = { klasse: "ls-rot", text: "Kein gültiges Mandat" };
  else zustand = { klasse: "ls-rot", text: "Mandat bei Qonto nicht angelegt" };

  // Nachlegen nur, wenn der Einzug wirklich über Qonto läuft — sonst lehnt der
  // Server ab, und der Knopf wäre eine Falle (Testlauf 06.10.2026).
  const ueberQonto = data.einzugsweg === "qonto";
  const nachholen = ueberQonto && data.qonto_verbunden && (!m || (m.weg === "qonto" && !m.qonto_status));

  async function kopieren(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2500);
    } catch {
      setKopiert(false);
    }
  }

  return (
    <div className="pf-card ls-karte">
      <div className="ls-kopf">
        <span className="ls-titel">SEPA-Lastschrift</span>
        <span className={`ls-marke ${zustand.klasse}`}>{zustand.text}</span>
      </div>

      {m && m.weg === "qonto" && (
        <div className="ls-zeilen">
          <div><span>Mandatsreferenz</span><b>{m.referenz || "—"}</b></div>
          {m.unterschrieben_am && <div><span>Unterschrieben</span><b>{tag(m.unterschrieben_am)}</b></div>}
          {m.erste_faellig && (
            <div><span>Erste Rate (zieht Qonto selbst ein)</span><b>{euro(m.erste_betrag)} am {tag(m.erste_faellig)}</b></div>
          )}
        </div>
      )}

      {m?.sign_url && (
        <>
          <p className="ls-text">
            Der Kunde hat den Link per Mail bekommen, von uns und von Qonto. Findet er ihn nicht, schick ihm diesen:
          </p>
          <div className="ls-link">{m.sign_url}</div>
          <button type="button" className="pf-btn ls-knopf" onClick={() => kopieren(m.sign_url as string)}>
            {kopiert ? "Kopiert ✓" : "Link kopieren"}
          </button>
        </>
      )}

      {m?.fehler && !m.qonto_status && (ueberQonto
        ? <p className="ls-fehler">Qonto hat das Mandat abgelehnt: {m.fehler}</p>
        : <p className="ls-text">
            Dieser Vertrag sollte über Qonto eingezogen werden, Qonto hat das Mandat aber abgelehnt. Der Einzug läuft
            nicht mehr über Qonto: den Kunden um ein Mandat für die Einzugsdatei bitten oder auf Überweisung umstellen.
          </p>)}

      {nachholen && (
        <button type="button" className="pf-btn ls-knopf" disabled={anlegen.isPending} onClick={() => anlegen.mutate()}>
          {anlegen.isPending ? "Legt an …" : "Mandat bei Qonto anlegen"}
        </button>
      )}
      {ueberQonto && !data.qonto_verbunden && m?.weg !== "eigen" && (
        <p className="ls-text">Qonto ist nicht verbunden — Seite „Lastschrift“ → „Mit Qonto verbinden“.</p>
      )}
      {anlegen.error && <p className="ls-fehler">{fehlertext(anlegen.error) || "Fehlgeschlagen."}</p>}
    </div>
  );
}
